import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowRight, Target, Zap, Brain, BarChart3, Rocket, Users, Trophy, TrendingUp, Lightbulb } from 'lucide-react';
import { useFadeIn } from '../hooks/useFadeIn';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation, translations } from '../translations';
import LanguageSwitcher from './LanguageSwitcher';

const LandingPage = () => {
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const philosophyRef = useRef(null);
  const servicesRef = useRef(null);
  const formulaRef = useRef(null);
  const edgeRef = useRef(null);
  const ctaRef = useRef(null);
  const contactRef = useRef(null);
  const navRef = useRef(null);

  // Animation refs using custom hook
  const [heroTitleRef, heroTitleVisible] = useFadeIn({ delay: 100 });
  const [heroSubtitleRef, heroSubtitleVisible] = useFadeIn({ delay: 300 });
  const [heroCtaRef, heroCtaVisible] = useFadeIn({ delay: 500 });
  const [heroStatsRef, heroStatsVisible] = useFadeIn({ delay: 700 });
  const [videoRef, videoVisible] = useFadeIn({ delay: 200 });
  const [videoTitleRef, videoTitleVisible] = useFadeIn({ delay: 400 });
  const [statsCardsRef, statsCardsVisible] = useFadeIn({ delay: 200 });
  const [whoWeAreTextRef, whoWeAreTextVisible] = useFadeIn({ delay: 300 });
  const [whoWeAreImageRef, whoWeAreImageVisible] = useFadeIn({ delay: 400 });
  const [philosophyTitleRef, philosophyTitleVisible] = useFadeIn({ delay: 100 });
  const [philosophyCardsRef, philosophyCardsVisible] = useFadeIn({ delay: 300 });
  const [servicesTitleRef, servicesTitleVisible] = useFadeIn({ delay: 100 });
  const [servicesContentRef, servicesContentVisible] = useFadeIn({ delay: 300 });
  const [formulaTitleRef, formulaTitleVisible] = useFadeIn({ delay: 100 });
  const [formulaCardsRef, formulaCardsVisible] = useFadeIn({ delay: 300 });
  const [edgeTitleRef, edgeTitleVisible] = useFadeIn({ delay: 100 });
  const [edgeItemsRef, edgeItemsVisible] = useFadeIn({ delay: 300 });
  const [clientsTitleRef, clientsTitleVisible] = useFadeIn({ delay: 100 });
  const [clientsLogosRef, clientsLogosVisible] = useFadeIn({ delay: 300 });
  const [ctaTitleRef, ctaTitleVisible] = useFadeIn({ delay: 100 });
  const [ctaButtonRef, ctaButtonVisible] = useFadeIn({ delay: 300 });
  const [contactTitleRef, contactTitleVisible] = useFadeIn({ delay: 100 });
  const [contactFormRef, contactFormVisible] = useFadeIn({ delay: 300 });

  // Language context
  const { currentLanguage } = useLanguage();
  const t = (key) => getTranslation(currentLanguage, key);

  // State for navigation visibility and active service
  const [showNavigation, setShowNavigation] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  // Services data - now dynamic based on language with useMemo
  const services = React.useMemo(() => {
    const serviceItems = currentLanguage === 'en' 
      ? translations.en.services.items 
      : translations.me.services.items;
    
    return serviceItems.map((service, index) => ({
      icon: [
        <Lightbulb className="w-8 h-8" />,
        <Target className="w-8 h-8" />,
        <Zap className="w-8 h-8" />,
        <Rocket className="w-8 h-8" />
      ][index],
      title: service.title,
      description: service.description,
      detail: service.detail,
      benefits: service.benefits,
      image: [
        'https://imagedelivery.net/bsJiXVjZz9CYOTIw_HoJ7g/5af11ec5-ee37-40c1-51b9-070d2987ba00/public',
        'https://agents.beunframed.com/wp-content/uploads/2025/08/image-30.webp',
        'https://agents.beunframed.com/wp-content/uploads/2025/08/image-31.webp',
        'https://agents.beunframed.com/wp-content/uploads/2025/08/image-32.webp'
      ][index]
    }));
  }, [currentLanguage]);

  useEffect(() => {
    // Scroll event listener for navigation visibility
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.offsetTop + heroRef.current.offsetHeight;
        const scrollPosition = window.scrollY;
        
        // Much more aggressive detection for mobile due to smaller hero
        const isMobile = window.innerWidth < 768;
        const threshold = isMobile ? heroBottom - 100 : heroBottom - 50;
        
        if (scrollPosition > threshold) {
          setShowNavigation(true);
        } else {
          setShowNavigation(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Intersection Observer for animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    const sections = [heroRef, statsRef, philosophyRef, servicesRef, formulaRef, edgeRef, ctaRef];
    sections.forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const scrollToContact = () => {
    contactRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Slider navigation functions
  const goToPrevService = () => {
    setActiveService(prev => prev === 0 ? services.length - 1 : prev - 1);
  };

  const goToNextService = () => {
    setActiveService(prev => (prev + 1) % services.length);
  };

  const goToService = (index) => {
    setActiveService(index);
  };

  // Touch/swipe functionality
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNextService();
    }
    if (isRightSwipe) {
      goToPrevService();
    }
  };

  // Form handling
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone || !formData.company || !formData.message) {
      alert('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Send to webhook
      const response = await fetch('https://hook.eu2.make.com/1uuczia311qtiyxqoldfrvfe30jx3vgr', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        // Show thank you message
        setShowThankYou(true);
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      alert('There was an error submitting your form. Please try again.');
    }

    setIsSubmitting(false);
  };

  const formula = React.useMemo(() => {
    return t('formula.items').map((item, index) => ({
      step: item.step,
      title: item.title,
      description: item.description
    }));
  }, [currentLanguage, t]);

  const edges = React.useMemo(() => t('edge.items'), [currentLanguage, t]);

  return (
    <div className="min-h-screen bg-white">
      {/* Language Switcher - Always visible on top right */}
      <div className="fixed top-4 md:top-6 right-4 md:right-6 z-[70]">
        <LanguageSwitcher />
      </div>

      {/* Header - Floating logo on top left when scrolled */}
      <header 
        ref={navRef}
        className={`fixed top-4 md:top-6 left-4 md:left-6 z-[60] transition-all duration-500 ${
          showNavigation ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'
        }`}
      >
        <div 
          className="bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 md:px-8 py-4 md:py-5 shadow-2xl cursor-pointer transition-all duration-300 hover:bg-white/20 hover:scale-105 relative z-10"
        >
          <div className="flex justify-center items-center">
            <img 
              src="https://agents.beunframed.com/wp-content/uploads/2025/07/Full-Logo-white-SVG-2.svg" 
              alt="Unframed" 
              className="h-6 md:h-8 w-auto pointer-events-none"
              style={{ transition: 'none', transform: 'none' }}
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="hero-section pt-8 md:pt-12 pb-16 md:pb-20 px-6 bg-jungle-green text-white relative overflow-hidden"
      >
        {/* Hero Background with SVG overlay */}
        <div className="absolute inset-0 z-0">
          {/* Keep the dark green background */}
          <div className="absolute inset-0 bg-jungle-green"></div>
          {/* Your SVG image overlay */}
          <img 
            src="https://agents.beunframed.com/wp-content/uploads/2025/07/Untitled-design-30.svg"
            alt="Hero Background" 
            className="absolute inset-0 w-full h-full object-cover opacity-30"
            loading="eager"
            decoding="async"
          />
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10 w-full">
          <div className="hero-content py-8 md:py-12">
            {/* White Logo on Hero Section - Bigger */}
            <div 
              ref={heroTitleRef}
              className={`mb-12 fade-in ${heroTitleVisible ? 'visible' : ''}`}
            >
              <img 
                src="https://agents.beunframed.com/wp-content/uploads/2025/07/Full-Logo-white-SVG.svg" 
                alt="Unframed" 
                className="h-16 md:h-20 mx-auto"
                loading="eager"
                decoding="async"
              />
            </div>
            
            <h1 
              ref={heroSubtitleRef}
              className={`hero-headline text-6xl md:text-8xl font-clash font-bold mb-6 leading-tight fade-in-up ${heroSubtitleVisible ? 'visible' : ''}`}
            >
              {t('hero.tagline')}
              <br />
              <span className="kinetic-text">{t('hero.tagline2')}</span>
            </h1>
            <p 
              ref={heroCtaRef}
              className={`text-xl md:text-2xl font-geist mb-8 max-w-3xl mx-auto opacity-90 fade-in-up delay-200 ${heroCtaVisible ? 'visible' : ''}`}
            >
              {t('hero.subtitle')}
            </p>
            <div
              ref={heroCtaRef}
              className={`fade-in-up delay-400 ${heroCtaVisible ? 'visible' : ''}`}
            >
              <Button 
                onClick={scrollToContact}
                size="lg"
                className="bg-ogre-orange hover:bg-ogre-orange-dark text-white px-8 py-4 rounded-lg font-geist font-medium text-lg transition-all duration-300 hover:shadow-xl hover:scale-105 pulse-cta"
              >
                {t('hero.cta')}
                <ArrowRight className="ml-3 w-5 h-5" />
              </Button>
            </div>
            
            {/* Stats as Horizontal Badges */}
            <div 
              ref={heroStatsRef}
              className={`mt-12 flex flex-wrap justify-center gap-4 md:gap-6 fade-in-up delay-600 ${heroStatsVisible ? 'visible' : ''}`}
            >
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 text-sm font-geist font-medium">
                {t('hero.stats.stat1')}
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 text-sm font-geist font-medium">
                {t('hero.stats.stat2')}
              </div>
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-3 text-sm font-geist font-medium">
                {t('hero.stats.stat3')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-12 px-6 bg-gradient-to-br from-jungle-green/5 to-jungle-green/10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Video Container */}
          <div 
            ref={videoRef}
            className={`mb-6 fade-in-up ${videoVisible ? 'visible' : ''}`}
          >
            {currentLanguage === 'en' ? (
              <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                <iframe
                  src="https://customer-bgwniqrr523xvn7e.cloudflarestream.com/76c41e7e1df4a7eaec10172c2517fa1f/iframe?preload=true&poster=https%3A%2F%2Fcustomer-bgwniqrr523xvn7e.cloudflarestream.com%2F76c41e7e1df4a7eaec10172c2517fa1f%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
                  loading="lazy"
                  style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen={true}
                ></iframe>
              </div>
            ) : (
              <div style={{ position: 'relative', paddingTop: '56.25%' }}>
                <iframe
                  src="https://customer-bgwniqrr523xvn7e.cloudflarestream.com/bd86039aa7aac6c31e5b15f6bc5d3010/iframe?preload=true&poster=https%3A%2F%2Fcustomer-bgwniqrr523xvn7e.cloudflarestream.com%2Fbd86039aa7aac6c31e5b15f6bc5d3010%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600"
                  loading="lazy"
                  style={{ border: 'none', position: 'absolute', top: 0, left: 0, height: '100%', width: '100%' }}
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen={true}
                ></iframe>
              </div>
            )}
          </div>
          
          {/* Benjamin's Info */}
          <div 
            ref={videoTitleRef}
            className={`mb-8 fade-in-up delay-200 ${videoTitleVisible ? 'visible' : ''}`}
          >
            <h3 className="text-2xl md:text-3xl font-clash font-bold text-jungle-green mb-2">
              Benjamin
            </h3>
            <p className="text-lg font-geist text-gray-600">
              CEO & Co-Founder
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section ref={statsRef} className="py-20 px-6" style={{ backgroundColor: '#D5E2E2' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              ref={statsCardsRef}
              className={`text-4xl md:text-5xl font-clash font-bold text-jungle-green mb-12 fade-in-up ${statsCardsVisible ? 'visible' : ''}`}
            >
              {t('whoWeAre.title')}
            </h2>
          </div>
          
          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Text */}
            <div 
              ref={whoWeAreTextRef}
              className={`fade-in-left ${whoWeAreTextVisible ? 'visible' : ''}`}
            >
              <p className="text-lg md:text-xl font-geist text-gray-700 leading-relaxed mb-6">
                {t('whoWeAre.intro')}
              </p>
              
              <div className="mb-6">
                <p className="text-lg md:text-xl font-geist text-gray-700 leading-relaxed mb-4">
                  {t('whoWeAre.supportText')}
                </p>
                <ul className="space-y-2">
                  {t('whoWeAre.services').map((service, index) => (
                    <li key={index} className="flex items-center space-x-3">
                      <div className="w-2 h-2 bg-ogre-orange rounded-full flex-shrink-0"></div>
                      <span className="text-lg font-geist text-gray-700">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <p className="text-lg md:text-xl font-geist text-gray-700 leading-relaxed mb-6">
                {t('whoWeAre.vision')}
              </p>
              
              <p className="text-lg md:text-xl font-geist text-jungle-green font-medium">
                {t('whoWeAre.goal')}
              </p>
            </div>
            
            {/* Right Side - Image */}
            <div 
              ref={whoWeAreImageRef}
              className={`fade-in-right ${whoWeAreImageVisible ? 'visible' : ''}`}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={t('whoWeAre.image')} 
                  alt="Who We Are - Team"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  decoding="async"
                  width="600"
                  height="400"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Enhanced Split Screen Slider */}
      <section ref={servicesRef} id="services" className="py-20 px-6 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              ref={servicesTitleRef}
              className={`text-4xl md:text-5xl font-clash font-bold text-jungle-green mb-8 fade-in-up ${servicesTitleVisible ? 'visible' : ''}`}
            >
              {t('services.title')}
            </h2>
          </div>
          
          {/* Split Screen Layout with Touch Support */}
          <div 
            ref={servicesContentRef}
            className={`grid lg:grid-cols-2 gap-12 items-center min-h-[600px] relative fade-in-up delay-200 ${servicesContentVisible ? 'visible' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            
            {/* Left Side - Large Featured Visual with Navigation */}
            <div className="relative h-[500px] md:h-[600px] rounded-2xl overflow-hidden group">
              <div className="absolute inset-0 bg-jungle-green rounded-2xl"></div>
              
              {/* Featured Service Image */}
              <div className="absolute inset-0 transition-all duration-700 ease-in-out">
                <img 
                  src={services[activeService].image}
                  alt={services[activeService].title}
                  className="w-full h-full object-cover opacity-30 transition-all duration-700"
                  loading="lazy"
                  decoding="async"
                  width="600"
                  height="600"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-jungle-green/80 to-jungle-green/40"></div>
              </div>
              
              {/* Navigation Arrows */}
              <button
                onClick={goToPrevService}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all duration-300 hover:scale-110 z-10"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <button
                onClick={goToNextService}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-3 transition-all duration-300 hover:scale-110 z-10"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              
              {/* Featured Service Icon & Title Overlay */}
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-8">
                <div className="text-ogre-orange mb-8 transform transition-all duration-700 scale-110">
                  <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center">
                    {services[activeService].icon}
                  </div>
                </div>
                <h3 className="text-4xl md:text-5xl font-clash font-bold text-center mb-4 transition-all duration-700">
                  {services[activeService].title}
                </h3>
                <p className="text-xl md:text-2xl font-geist text-ogre-orange font-medium text-center transition-all duration-700">
                  {services[activeService].description}
                </p>
              </div>
              
              {/* Animated Border */}
              <div className="absolute inset-0 rounded-2xl border-2 border-ogre-orange/20 transition-all duration-700 group-hover:border-ogre-orange/60"></div>
            </div>
            
            {/* Right Side - Rotating Content */}
            <div className="space-y-8">
              
              {/* Service Navigation Dots */}
              <div className="flex justify-center lg:justify-start space-x-4 mb-8">
                {services.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToService(index)}
                    className={`w-4 h-4 rounded-full transition-all duration-300 ${
                      activeService === index 
                        ? 'bg-ogre-orange scale-125' 
                        : 'bg-gray-300 hover:bg-ogre-orange/50'
                    }`}
                  />
                ))}
              </div>
              
              {/* Active Service Content */}
              <div className="transition-all duration-700 ease-in-out transform">
                <div className="mb-8">
                  <h4 className="text-3xl md:text-4xl font-clash font-bold text-jungle-green mb-4">
                    {services[activeService].title}
                  </h4>
                  <p className="text-xl text-ogre-orange font-geist font-semibold mb-6">
                    {services[activeService].description}
                  </p>
                  <p className="text-lg text-gray-700 font-geist leading-relaxed mb-8">
                    {services[activeService].detail}
                  </p>
                </div>
                
                {/* Service Features/Benefits */}
                <div className="space-y-4">
                  <h5 className="text-xl font-clash font-bold text-jungle-green mb-4">{t('services.key_benefits_label')}</h5>
                  <div className="space-y-3">
                    {services[activeService]?.benefits?.map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-ogre-orange rounded-full"></div>
                        <span className="font-geist text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Swipe Instruction for Mobile */}
              <div className="block lg:hidden text-center mt-6">
                <p className="text-sm text-gray-500 font-geist">
                  👈 Swipe left or right to navigate services 👉
                </p>
              </div>
              
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section ref={philosophyRef} id="philosophy" className="py-20 px-6 bg-jungle-green-light relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <picture>
            <img 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHx0ZWFtd29ya3xlbnwwfHx8fDE3NTUxNjg1NDh8MA&ixlib=rb-4.1.0&q=85"
              alt="Team Collaboration"
              className="w-full h-full object-cover opacity-10"
              loading="lazy"
              decoding="async"
              width="1200"
              height="800"
            />
          </picture>
          <div className="absolute inset-0 bg-jungle-green-light opacity-95"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 
              ref={philosophyTitleRef}
              className={`text-4xl md:text-5xl font-clash font-bold text-jungle-green mb-8 fade-in-up ${philosophyTitleVisible ? 'visible' : ''}`}
            >
              {t('philosophy.title')}
            </h2>
          </div>
          <div 
            ref={philosophyCardsRef}
            className={`grid md:grid-cols-3 gap-8 fade-in-up delay-200 ${philosophyCardsVisible ? 'visible' : ''}`}
          >
            {t('philosophy.cards').map((card, index) => (
              <div 
                key={index}
                className={`philosophy-card text-center p-8 bg-white/80 backdrop-blur-sm rounded-lg hover:bg-white hover:shadow-lg transition-all duration-300 fade-in-${['left', 'up', 'right'][index]} delay-${100 + (index * 100)} ${philosophyCardsVisible ? 'visible' : ''}`}
                style={{ transitionDelay: `${100 + (index * 100)}ms` }}
              >
                <div className="w-16 h-16 bg-ogre-orange rounded-full flex items-center justify-center mx-auto mb-6">
                  {[<BarChart3 className="w-8 h-8 text-white" />, <Brain className="w-8 h-8 text-white" />, <Users className="w-8 h-8 text-white" />][index]}
                </div>
                <h3 className="text-2xl font-clash font-bold text-jungle-green mb-4">
                  {card.title}
                </h3>
                <p className="font-geist text-gray-700">
                  {card.description}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center max-w-4xl mx-auto">
            <p className="text-lg font-geist text-gray-700 leading-relaxed bg-white/60 backdrop-blur-sm p-6 rounded-lg">
              {t('philosophy.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Formula Section */}
      <section ref={formulaRef} id="formula" className="py-20 px-6 bg-gray-50 relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <picture>
            <img 
              src="https://agents.beunframed.com/wp-content/uploads/2025/08/image-33.webp"
              alt="Unframed Formula Background"
              className="w-full h-full object-cover opacity-5"
              loading="lazy"
              decoding="async"
              width="1200"
              height="800"
            />
          </picture>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 
              ref={formulaTitleRef}
              className={`text-4xl md:text-5xl font-clash font-bold text-jungle-green mb-8 fade-in-up ${formulaTitleVisible ? 'visible' : ''}`}
            >
              {t('formula.title')}
            </h2>
          </div>
          <div 
            ref={formulaCardsRef}
            className={`grid md:grid-cols-2 gap-8 fade-in-up delay-200 ${formulaCardsVisible ? 'visible' : ''}`}
          >
            {formula.map((item, index) => (
              <div 
                key={index} 
                className={`formula-step text-left p-8 bg-white rounded-lg hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-2 border-transparent hover:border-ogre-orange group fade-in-up delay-${100 + (index * 100)} ${formulaCardsVisible ? 'visible' : ''}`}
                style={{ transitionDelay: `${100 + (index * 100)}ms` }}
              >
                <div className="flex items-start space-x-6">
                  {/* Step Number */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-ogre-orange text-white rounded-full flex items-center justify-center group-hover:bg-jungle-green transition-colors duration-300">
                      <span className="text-2xl font-clash font-bold">{item.step}</span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1">
                    <h3 className="text-xl font-clash font-bold text-jungle-green mb-4 leading-tight">
                      {item.title}
                    </h3>
                    <p className="font-geist text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Edge Section */}
      <section ref={edgeRef} id="edge" className="py-20 px-6 bg-white relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <picture>
            <img 
              src="https://imagedelivery.net/bsJiXVjZz9CYOTIw_HoJ7g/f1ea04f4-4516-43a7-0b28-f0c7a63eb400/public"
              alt="Why Work With Us Background"
              className="w-full h-full object-cover opacity-5"
              loading="lazy"
              decoding="async"
              width="1200"
              height="800"
            />
          </picture>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 
              ref={edgeTitleRef}
              className={`text-4xl md:text-5xl font-clash font-bold text-jungle-green mb-8 fade-in-up ${edgeTitleVisible ? 'visible' : ''}`}
            >
              {t('edge.title')}
            </h2>
            <p 
              ref={edgeTitleRef}
              className={`text-xl font-geist text-gray-700 max-w-3xl mx-auto fade-in-up delay-200 ${edgeTitleVisible ? 'visible' : ''}`}
            >
              {t('edge.subtitle')}
            </p>
          </div>
          <div 
            ref={edgeItemsRef}
            className={`max-w-4xl mx-auto fade-in-up delay-300 ${edgeItemsVisible ? 'visible' : ''}`}
          >
            {edges.map((edge, index) => (
              <div 
                key={index} 
                className={`edge-item flex items-start space-x-4 mb-6 p-6 rounded-lg hover:bg-jungle-green-light transition-all duration-300 bg-white/60 backdrop-blur-sm border border-gray-100 hover:border-ogre-orange hover:shadow-lg fade-in-left delay-${100 + (index * 100)} ${edgeItemsVisible ? 'visible' : ''}`}
                style={{ transitionDelay: `${100 + (index * 100)}ms` }}
              >
                <div className="flex-shrink-0 w-12 h-12 bg-ogre-orange rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <p className="text-lg font-geist text-gray-700 font-medium pt-2">
                  {edge}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-20 px-6 relative overflow-hidden" style={{ backgroundColor: '#004747' }}>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 
              ref={clientsTitleRef}
              className={`text-4xl md:text-5xl font-clash font-bold text-white mb-8 fade-in-up ${clientsTitleVisible ? 'visible' : ''}`}
            >
              {t('clients.title')}
            </h2>
          </div>
          
          {/* Logo Grid */}
          <div 
            ref={clientsLogosRef}
            className={`grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-8 md:gap-12 items-center justify-items-center fade-in-up delay-200 ${clientsLogosVisible ? 'visible' : ''}`}
          >
            {t('clients.logos').map((logo, index) => (
              <div 
                key={index}
                className={`flex items-center justify-center p-4 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-all duration-300 hover:scale-105 fade-in-up delay-${50 + (index * 50)} ${clientsLogosVisible ? 'visible' : ''}`}
                style={{ transitionDelay: `${50 + (index * 20)}ms` }}
              >
                <img 
                  src={logo}
                  alt={`Client logo ${index + 1}`}
                  className="max-w-full max-h-16 w-auto h-auto object-contain filter brightness-0 invert opacity-80 hover:opacity-100 transition-opacity duration-300"
                  loading="lazy"
                  decoding="async"
                  width="120"
                  height="64"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 px-6 bg-white relative overflow-hidden">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 
            ref={ctaTitleRef}
            className={`text-4xl md:text-6xl font-clash font-bold mb-8 fade-in-up ${ctaTitleVisible ? 'visible' : ''}`}
            style={{ color: '#004747' }}
          >
            {t('cta.title')}
          </h2>
          <p 
            ref={ctaTitleRef}
            className={`text-xl font-geist mb-12 max-w-2xl mx-auto fade-in-up delay-200 ${ctaTitleVisible ? 'visible' : ''}`}
            style={{ color: '#004747', opacity: 0.8 }}
          >
            {t('cta.subtitle')}
          </p>
          <div
            ref={ctaButtonRef}
            className={`fade-in-up delay-400 ${ctaButtonVisible ? 'visible' : ''}`}
          >
            <Button 
              onClick={scrollToContact}
              size="lg"
              className="bg-ogre-orange hover:bg-ogre-orange-light hover:text-jungle-green text-white px-12 py-6 rounded-lg font-geist font-bold text-xl transition-all duration-300 hover:shadow-xl hover:scale-105 pulse-cta"
            >
              {t('cta.button')}
              <ArrowRight className="ml-3 w-6 h-6" />
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section ref={contactRef} className="py-20 px-6 bg-jungle-green-light" id="contact">
        <div className="max-w-2xl mx-auto text-center">
          <h2 
            ref={contactTitleRef}
            className={`text-3xl font-clash font-bold text-jungle-green mb-8 fade-in-up ${contactTitleVisible ? 'visible' : ''}`}
          >
            {t('contact.title')}
          </h2>
          
          {showThankYou ? (
            // Thank You Message
            <Card 
              ref={contactFormRef}
              className={`bg-white shadow-xl scale-in ${contactFormVisible ? 'visible' : ''}`}
            >
              <CardContent className="p-12 text-center">
                <div className="text-6xl mb-6">🎉</div>
                <h3 className="text-3xl font-clash font-bold text-jungle-green mb-4">
                  {t('contact.thank_you.title')}
                </h3>
                <p className="text-lg font-geist text-gray-700 mb-6">
                  {t('contact.thank_you.message')}
                </p>
                <p className="text-md font-geist text-ogre-orange font-medium">
                  {t('contact.thank_you.tagline')}
                </p>
                <Button 
                  onClick={() => setShowThankYou(false)}
                  className="mt-6 bg-jungle-green hover:bg-jungle-green/90 text-white py-2 px-6 rounded-lg font-geist font-medium transition-all duration-300"
                >
                  {t('contact.thank_you.button')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            // Contact Form
            <Card 
              ref={contactFormRef}
              className={`bg-white shadow-xl fade-in-up delay-200 ${contactFormVisible ? 'visible' : ''}`}
            >
              <CardContent className="p-8">
                <form onSubmit={handleFormSubmit} className="space-y-6">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder={t('contact.form.name')}
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg font-geist focus:outline-none focus:ring-2 focus:ring-jungle-green focus:border-transparent"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder={t('contact.form.email')}
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg font-geist focus:outline-none focus:ring-2 focus:ring-jungle-green focus:border-transparent"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      placeholder={t('contact.form.phone')}
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg font-geist focus:outline-none focus:ring-2 focus:ring-jungle-green focus:border-transparent"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="company"
                      placeholder={t('contact.form.company')}
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg font-geist focus:outline-none focus:ring-2 focus:ring-jungle-green focus:border-transparent"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      rows="4"
                      placeholder={t('contact.form.message')}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg font-geist focus:outline-none focus:ring-2 focus:ring-jungle-green focus:border-transparent resize-none"
                    ></textarea>
                  </div>
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-ogre-orange hover:bg-ogre-orange-dark text-white py-3 rounded-lg font-geist font-medium text-lg transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : t('contact.form.submit')}
                  </Button>
                  <p className="text-sm text-gray-500 font-geist text-center">
                    {t('contact.form.required')}
                  </p>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-jungle-green text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <img 
              src="https://agents.beunframed.com/wp-content/uploads/2025/07/Full-Logo-white-SVG.svg" 
              alt="Unframed" 
              className="h-10 mx-auto mb-4"
            />
            <p className="font-geist text-lg opacity-80 font-medium">
              {t('footer.tagline')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;